import React, { useEffect, useState } from "react";
const API = process.env.REACT_APP_API_BASE_URL || "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

const dateOnly = (d) => String(d || "").slice(0, 10);
const dateObj = (d) => new Date(dateOnly(d) + "T00:00:00");

function ProcessModal({ settlement, onClose, onSuccess }) {
  const [method, setMethod] = useState("EFT");
  const [action, setAction] = useState("early_settle");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const methods = ["EFT", "ACH", "ACH_NOW", "INTERAC", "MANUAL"];
  const actions = [
    { value: "early_settle", label: "Early Settle", desc: "Collect payment before due date" },
    { value: "force_collect", label: "Force Collect", desc: "Force collection on overdue settlement" },
    { value: "mark_paid", label: "Mark Paid", desc: "Manually mark as paid (no provider call)" },
    { value: "waive", label: "Waive", desc: "Waive the settlement entirely" },
  ];

  const submit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/settlements/${settlement.id}/process`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ method, action, notes })
      });
      const d = await res.json();
      setResult(d);
      if (d.ok) onSuccess();
    } catch(e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">⚡ Process Settlement</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500">Settlement ID</span>
            <span className="font-mono text-xs">{settlement.id.substring(0,16)}...</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Amount Owing</span>
            <span className="font-bold text-red-600">${parseFloat(settlement.remainingAmount).toFixed(2)} CAD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Due Date</span>
            <span className={dateObj(settlement.dueDate) < dateObj(new Date().toISOString()) ? "text-red-600 font-semibold" : "text-gray-700"}>
              {dateOnly(settlement.dueDate)}
            </span>
          </div>
        </div>

        {!result ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                {methods.map(m => (
                  <button key={m}
                    onClick={() => setMethod(m)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${method === m ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                  >{m}</button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {method === 'EFT' && 'ZumRails — 2 business days'}
                {method === 'ACH' && 'Sila Money — 3 business days'}
                {method === 'ACH_NOW' && 'Sila RTP — same day'}
                {method === 'INTERAC' && 'ZumRails Interac — instant'}
                {method === 'MANUAL' && 'Manual override — no provider call'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <div className="space-y-2">
                {actions.map(a => (
                  <label key={a.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${action === a.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                    <input type="radio" name="action" value={a.value} checked={action === a.value} onChange={() => setAction(a.value)} className="mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">{a.label}</div>
                      <div className="text-xs text-gray-500">{a.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Reason for manual processing..."
                className="w-full border rounded-lg p-2 text-sm resize-none" rows={2}
              />
            </div>

            <button
              onClick={submit} disabled={loading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? "Processing..." : `⚡ ${actions.find(a=>a.value===action)?.label} via ${method}`}
            </button>
          </>
        ) : (
          <div className={`rounded-lg p-4 ${result.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {result.ok ? (
              <>
                <p className="font-semibold text-green-800 mb-2">✅ {result.message}</p>
                <div className="text-sm text-green-700 space-y-1">
                  <div>Provider: <span className="font-medium">{result.result?.provider}</span></div>
                  <div>Method: <span className="font-medium">{result.result?.method}</span></div>
                  {result.result?.estimatedDays > 0 && <div>Est. settlement: <span className="font-medium">{result.result.estimatedDays} business day(s)</span></div>}
                  {result.result?.estimatedDays === 0 && <div className="font-medium text-green-600">⚡ Instant settlement</div>}
                </div>
                <button onClick={onClose} className="mt-3 w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Done</button>
              </>
            ) : (
              <>
                <p className="font-semibold text-red-800 mb-1">❌ Failed</p>
                <p className="text-sm text-red-700">{result.error}</p>
                <button onClick={() => setResult(null)} className="mt-3 w-full py-2 bg-red-600 text-white rounded-lg text-sm font-medium">Try Again</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



function RescheduleModal({ settlement, onClose, onSuccess }) {
  const currentDue = settlement?.dueDate ? dateOnly(settlement.dueDate) : "";
  const [newDueDate, setNewDueDate] = useState(currentDue);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/settlements/${settlement.id}/reschedule`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ newDueDate, reason })
      });
      const d = await res.json();
      setResult(d);
      if (d.ok) onSuccess();
    } catch(e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">📅 Reschedule EFT</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500">Settlement ID</span>
            <span className="font-mono text-xs">{settlement.id.substring(0,16)}...</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Amount Owing</span>
            <span className="font-bold text-red-600">${parseFloat(settlement.remainingAmount || 0).toFixed(2)} CAD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Current Due Date</span>
            <span>{dateOnly(settlement.dueDate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Extensions Used</span>
            <span className="font-semibold">{settlement.extensionCount || settlement.extension_count || 0}/2</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Extension Fee</span>
            <span className="font-bold text-blue-700">${((((settlement.extensionCount || settlement.extension_count || 0) + 1) * 2)).toFixed(2)} CAD</span>
          </div>
        </div>

        {!result ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Due Date</label>
            <input
              type="date"
              value={newDueDate}
              onChange={e => setNewDueDate(e.target.value)}
              className="w-full border rounded-lg p-2 text-sm mb-4"
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Example: Customer requested date change due to payroll timing"
              className="w-full border rounded-lg p-2 text-sm resize-none mb-4"
              rows={3}
            />

            <button
              onClick={submit}
              disabled={loading || !newDueDate}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Rescheduling..." : "📅 Reschedule and Email Customer"}
            </button>
          </>
        ) : (
          <div className={`rounded-lg p-4 ${result.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {result.ok ? (
              <>
                <p className="font-semibold text-green-800 mb-2">✅ {result.message}</p>
                <p className="text-sm text-green-700">Customer email: {result.email?.success ? "sent" : "not sent / check logs"}</p>
                <button onClick={onClose} className="mt-3 w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Done</button>
              </>
            ) : (
              <>
                <p className="font-semibold text-red-800 mb-1">❌ Failed</p>
                <p className="text-sm text-red-700">{result.error}</p>
                <button onClick={() => setResult(null)} className="mt-3 w-full py-2 bg-red-600 text-white rounded-lg text-sm font-medium">Try Again</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default function CockpitCollections() {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, active: 0, settled: 0, overdue: 0, outstanding: 0 });
  const [processing, setProcessing] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(1, Math.ceil(settlements.length / pageSize));
  const pagedSettlements = settlements.slice((page - 1) * pageSize, page * pageSize);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus === "overdue") params.set("overdue", "true");
    else if (filterStatus) params.set("status", filterStatus);
    if (search.trim()) params.set("search", search.trim());
    const q = params.toString() ? `?${params.toString()}` : "";
    fetch(`${API}/api/v2/cockpit/settlements${q}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          const list = d.settlements || [];
          setSettlements(list);
          const now = new Date();
          const active = list.filter(s => s.status === 'active');
          const overdue = active.filter(s => dateObj(s.dueDate) < dateObj(now.toISOString()));
          setSummary({
            total: list.length,
            active: active.length,
            settled: list.filter(s => s.status === 'paid').length,
            overdue: overdue.length,
            outstanding: active.reduce((sum, s) => sum + parseFloat(s.remainingAmount || 0), 0)
          });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { setPage(1); load(); }, [filterStatus, search]);

  const statusColor = (s, dueDate) => {
    if (s === 'paid') return 'bg-green-100 text-green-800';
    if (s === 'waived') return 'bg-gray-100 text-gray-600';
    if (s === 'processing') return 'bg-blue-100 text-blue-800';
    if (dateObj(dueDate) < dateObj(new Date().toISOString())) return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const statusLabel = (s, dueDate) => {
    if (s === 'paid') return 'PAID';
    if (s === 'waived') return 'WAIVED';
    if (s === 'processing') return 'PROCESSING';
    if (dateObj(dueDate) < dateObj(new Date().toISOString())) return 'OVERDUE';
    return 'ACTIVE';
  };

  const daysLeft = (dueDate, status) => {
    if (status === 'paid') return <span className="text-green-700 font-semibold">Settled</span>;
    if (status === 'waived') return <span className="text-gray-500">Waived</span>;
    if (status === 'processing') return <span className="text-blue-700 font-semibold">Processing</span>;
    const diff = Math.ceil((dateObj(dueDate) - dateObj(new Date().toISOString())) / (1000 * 60 * 60 * 24));
    if (diff < 0) return <span className="text-red-600 font-bold">{Math.abs(diff)}d overdue</span>;
    if (diff === 0) return <span className="text-red-600 font-bold">Due today</span>;
    return <span className="text-gray-600">{diff}d left</span>;
  };

  const canProcess = (s) => !['paid', 'waived'].includes(s.status);

  return (
    <div className="p-6 space-y-6">
      {processing && (
        <ProcessModal
          settlement={processing}
          onClose={() => setProcessing(null)}
          onSuccess={() => { setProcessing(null); load(); }}
        />
      )}

      {rescheduling && (
        <RescheduleModal
          settlement={rescheduling}
          onClose={() => setRescheduling(null)}
          onSuccess={() => { setRescheduling(null); load(); }}
        />
      )}

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">📋 SNPL Collections</h1>
          <p className="text-gray-500 text-sm">Deferred settlement tracker · Manual processing via EFT / ACH / Interac</p>
        </div>
        <div className="flex gap-2">
          {["", "active", "processing", "paid", "overdue"].map(s => (
            <button key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filterStatus === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
            >{s === "" ? "All" : s === "overdue" ? "Overdue" : s.charAt(0).toUpperCase() + s.slice(1)}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-lg p-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, reference, settlement ID..."
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Settlements", value: summary.total, color: "bg-gray-50 border-gray-200" },
          { label: "Active", value: summary.active, color: "bg-yellow-50 border-yellow-200" },
          { label: "Settled/Paid", value: summary.settled, color: "bg-green-50 border-green-200" },
          { label: "Overdue", value: summary.overdue, color: "bg-red-50 border-red-200" },
          { label: "Outstanding (CAD)", value: `$${summary.outstanding.toFixed(2)}`, color: "bg-orange-50 border-orange-200" },
        ].map(c => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.color}`}>
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between bg-white border rounded-lg p-3 text-sm">
        <div className="text-gray-600">
          Showing {settlements.length === 0 ? 0 : ((page - 1) * pageSize) + 1}
          - {Math.min(page * pageSize, settlements.length)} of {settlements.length}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-gray-600">Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="border rounded px-2 py-1"
          >
            {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>

          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>

          <span className="text-gray-700">Page {page} of {totalPages}</span>

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {["User","Reference","Total Owed","Remaining","Due Date","Days","Extensions","Fees","Status","Action"].map(h => (
                <th key={h} className="px-4 py-3 text-left text-gray-600 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>}
            {!loading && settlements.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No settlements found</td></tr>}
            {pagedSettlements.map(s => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium">{s.user?.firstName || s.transaction?.user?.firstName || '—'} {s.user?.lastName || s.transaction?.user?.lastName || ''}</div>
                  <div className="text-xs text-gray-400">{s.user?.email || s.transaction?.user?.email || s.userId?.substring(0,8)+'...'}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs font-semibold text-blue-700">{s.transaction?.reference || s.reference || '—'}</div>
                  <div className="text-xs text-gray-400">{s.id?.substring ? s.id.substring(0,8) + '...' : s.id}</div>
                </td>
                <td className="px-4 py-3 font-bold">${parseFloat(s.totalAmount || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-red-600 font-semibold">${parseFloat(s.remainingAmount || 0).toFixed(2)}</td>
                <td className="px-4 py-3">{dateOnly(s.dueDate)}</td>
                <td className="px-4 py-3">{daysLeft(s.dueDate, s.status)}</td>
                <td className="px-4 py-3 text-sm font-semibold">{s.extensionCount || s.extension_count || 0}/2</td>
                <td className="px-4 py-3 text-sm">${2.00.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${statusColor(s.status, s.dueDate)}`}>
                    {statusLabel(s.status, s.dueDate)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {canProcess(s) ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setProcessing(s)}
                        className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap"
                      >
                        ⚡ Process
                      </button>
                      <button
                        onClick={() => setRescheduling(s)}
                        className="text-xs bg-white text-blue-700 border border-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-50 font-medium whitespace-nowrap"
                      >
                        📅 Reschedule
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
